/**
 * Delete a project — a port of `bimgo-ai-web`'s `DeleteProjectModal`.
 *
 * Same confirmation as the web client, deliberately: the red warning disc
 * beside "Confirm Delete Project", the "this cannot be undone" line, a summary
 * box naming the project (and its description, when it has one) so there is no
 * doubt which row is about to go, then Cancel and a red "Confirm Delete".
 *
 * An earlier revision here demanded the project's name be typed before the
 * button would act. That is stricter than the web client and was dropped for
 * parity — the two clients now ask the same question the same way.
 *
 * What is kept from that revision is failure handling: the web client sends the
 * error to `console.error` and closes as though the delete worked, leaving the
 * row on screen until a refetch contradicts it. Here the dialog stays open with
 * what the server actually said.
 */
import { useEffect, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { Modal } from "../settings/Modal";
import type { Project } from "../../lib/projects";

function AlertTriangleIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

export function DeleteProjectModal({
  project,
  onClose,
  onDelete,
}: {
  /** The row to delete; the dialog is open whenever this is set. */
  project: Project | null;
  onClose: () => void;
  onDelete: (projectId: string) => Promise<void>;
}) {
  const { t } = useLocale();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear between openings, so one project's failure is not shown over the next.
  useEffect(() => {
    setError(null);
    setBusy(false);
  }, [project?.id]);

  async function run() {
    if (!project || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onDelete(project.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={project !== null}
      onClose={() => (busy ? undefined : onClose())}
      title={t("projects.confirmDelete")}
      description={t("projects.deleteWarning")}
      icon={
        <span className="modal-icon-disc is-danger">
          <AlertTriangleIcon />
        </span>
      }
      footer={
        <>
          <button
            type="button"
            className="settings-btn secondary"
            onClick={onClose}
            disabled={busy}
          >
            {t("projects.cancel")}
          </button>
          <button
            type="button"
            className="settings-btn is-danger"
            onClick={() => void run()}
            disabled={busy}
          >
            <TrashIcon />
            {t("projects.confirmDeleteBtn")}
          </button>
        </>
      }
    >
      {project ? (
        <div className="danger-summary">
          <div className="danger-summary-label">{t("projects.projectNameLabel")}</div>
          <div className="danger-summary-value">{project.name}</div>
          {project.description ? (
            <>
              <div className="danger-summary-label">{t("projects.descriptionLabel")}</div>
              <div className="danger-summary-value">{project.description}</div>
            </>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <p className="settings-msg err" role="alert">
          {error}
        </p>
      ) : null}
    </Modal>
  );
}
